import { Link } from "react-router-dom"
import { motion } from "motion/react"
import PostersBackground, { POSTERS_SECTION_MIN_H } from "../start/PostersBackground"
import { useCinemaHref } from "../../hooks/useCinemaHref"

const DISPLAY = "'Tilda Sans', sans-serif"
const BODY = "'Manrope', sans-serif"
const MONO = "'JetBrains Mono', monospace"

function Sticker({ label, color, rotate = 0 }: { label: string; color: string; rotate?: number }) {
    return (
        <span
            className="inline-flex items-center rounded-full border px-3 py-1 text-[11px] font-bold uppercase tracking-widest whitespace-nowrap select-none"
            style={{
                fontFamily: MONO,
                color,
                borderColor: `${color}66`,
                background: `${color}22`,
                transform: `rotate(${rotate}deg)`,
            }}>
            {label}
        </span>
    )
}

type TicketsEmptyHeroProps = {
    variant: "guest" | "empty"
    onLogin?: () => void
}

export default function TicketsEmptyHero({ variant, onLogin }: TicketsEmptyHeroProps) {
    const href = useCinemaHref()
    const afishaTo = href("/afisha")
    const isGuest = variant === "guest"

    return (
        <section className={`relative w-full overflow-hidden pb-20 md:pb-8 ${POSTERS_SECTION_MIN_H}`}>
            <PostersBackground mobileAlign="center" />

            <div className="relative z-10 mx-auto flex w-full max-w-[1240px] flex-col items-center px-4 py-12 text-center sm:px-8 sm:py-16 md:items-start md:text-left lg:min-h-[620px] lg:justify-center">
                <div className="flex w-full max-w-[560px] flex-col items-center gap-6 md:items-start">
                    <div className="flex flex-wrap justify-center gap-2.5 md:justify-start">
                        <Sticker label="афиша 🎬" color="#349be6" rotate={-2} />
                        <Sticker label="кинобонусы ✨" color="#29cc6a" rotate={2} />
                        <Sticker label="без очередей 🎟" color="#e4206b" rotate={-1} />
                    </div>

                    <div className="flex flex-col items-center gap-3 md:items-start">
                        <motion.h2
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.4 }}
                            className="text-white leading-[1.05]"
                            style={{
                                fontFamily: DISPLAY,
                                fontWeight: 800,
                                fontSize: "clamp(28px, 6vw, 44px)",
                                letterSpacing: "-0.02em",
                            }}>
                            {isGuest ? (
                                <>
                                    Мои <span style={{ color: "#349be6" }}>билеты</span>
                                </>
                            ) : (
                                <>
                                    Самое время <span style={{ color: "#349be6" }}>в кино!</span>
                                </>
                            )}
                        </motion.h2>
                        <motion.p
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.4, delay: 0.08 }}
                            className="mx-auto max-w-md text-[15px] leading-relaxed sm:text-[16px] md:mx-0"
                            style={{ fontFamily: BODY, fontWeight: 500, color: "rgba(244,246,255,0.68)" }}>
                            {isGuest
                                ? "Войди, чтобы увидеть купленные билеты и QR-коды для входа в зал"
                                : "Здесь появятся билеты после покупки — выбирай сеанс в афише и приходи без очередей"}
                        </motion.p>
                    </div>

                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.35, delay: 0.14 }}
                        className="flex w-full max-w-[320px] flex-col items-center gap-3 sm:max-w-none sm:flex-row sm:flex-wrap md:items-start">
                        {isGuest ? (
                            <>
                                <motion.button
                                    type="button"
                                    whileHover={{ scale: 1.03 }}
                                    whileTap={{ scale: 0.97 }}
                                    onClick={onLogin}
                                    className="inline-flex items-center justify-center rounded-full px-6 py-3 text-[13px] text-white"
                                    style={{
                                        background: "#e4206b",
                                        fontFamily: MONO,
                                        fontWeight: 700,
                                        boxShadow: "0 0 18px rgba(228,32,107,0.4)",
                                    }}>
                                    Войти по номеру
                                </motion.button>
                                <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                                    <Link
                                        to={afishaTo}
                                        className="inline-flex items-center justify-center rounded-full border border-white/12 bg-white/[0.04] px-6 py-3 text-[13px] text-white/55 transition-colors hover:border-white/20 hover:bg-white/[0.07] hover:text-white/75"
                                        style={{ fontFamily: MONO, fontWeight: 700, letterSpacing: "0.04em" }}>
                                        К афише
                                    </Link>
                                </motion.div>
                            </>
                        ) : (
                            <>
                                <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
                                    <Link
                                        to={afishaTo}
                                        className="inline-flex items-center justify-center rounded-full px-6 py-3 text-[13px] text-white"
                                        style={{
                                            background: "#e4206b",
                                            fontFamily: MONO,
                                            fontWeight: 700,
                                            boxShadow: "0 0 28px rgba(228,32,107,0.45)",
                                            letterSpacing: "0.04em",
                                        }}>
                                        За билетами!
                                    </Link>
                                </motion.div>
                                <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                                    <Link
                                        to="/profile/history"
                                        className="inline-flex items-center justify-center rounded-full border border-white/12 bg-white/[0.04] px-6 py-3 text-[13px] text-white/55 transition-colors hover:border-white/20 hover:bg-white/[0.07] hover:text-white/75"
                                        style={{ fontFamily: MONO, fontWeight: 700, letterSpacing: "0.04em" }}>
                                        История покупок
                                    </Link>
                                </motion.div>
                            </>
                        )}
                    </motion.div>
                </div>
            </div>
        </section>
    )
}
